/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * Redux store configuration module
 * Sets up the central Redux store with persistence and middleware
 * @module redux/store
 */

import { configureStore } from "@reduxjs/toolkit";
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from "redux-persist";
import storage from "redux-persist/lib/storage";

import persistConfig from "./get-persist-config";
import rootReducer from "./root-reducer";

/**
 * Configure the Redux store with persistence and middleware
 * @see https://redux-toolkit.js.org/usage/usage-guide#use-with-redux-persist
 *
 * - Disables immutableCheck for performance reasons
 * - Configures serializableCheck to ignore redux-persist actions
 * - Wraps the root reducer with persistence capabilities
 */
const store = configureStore({
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
  reducer: persistReducer(persistConfig({ storage }), rootReducer),
});

/**
 * Redux persistor instance used to persist and rehydrate store state
 * @type {import('redux-persist').Persistor}
 */
export const persistor = persistStore(store);

export default store;
